// Szkielety strony raportu: ten sam układ co Report.tsx (okruszki, panel boczny 280px, karty), szare paski w miejscu danych.
import { LoadingRegion, Skeleton, SkeletonLines } from "../../components/ui/Skeleton";

const card = "rounded-[10px] border border-[var(--sc-border)] bg-[var(--sc-surface)]";

// Nazwa raportu, ośrodek, daty, status i lista sekcji w panelu bocznym
export const ReportHeaderSkeleton = () => (
  <LoadingRegion label="Wczytywanie danych raportu">
    <div className="px-5 py-4 border-b border-[var(--sc-border)]">
      <Skeleton className="h-4 w-32 mb-2.5" />
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-3 w-44" />
        <Skeleton className="h-3 w-36" />
        <Skeleton className="h-3 w-28" />
      </div>
      <Skeleton className="h-6 w-24 mt-3 rounded-[20px]" />
    </div>
    <div className="py-3">
      <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--sc-muted)] px-5 mb-2">Sekcje raportu</div>
      {[70, 55, 80, 60, 65, 50].map((w, i) => (
        <div key={i} className="flex items-center gap-2.5 px-5 py-[8px]">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--sc-border)] flex-shrink-0" />
          <div style={{ width: `${w}%` }}>
            <Skeleton className="h-3" />
          </div>
        </div>
      ))}
    </div>
  </LoadingRegion>
);

// Zwinięta karta (historia statusów, metadane)
export const ReportCardSkeleton = ({ title }: { title?: string }) => (
  <LoadingRegion label={title ? `Wczytywanie: ${title}` : "Wczytywanie"} className={`${card} px-5 py-3.5 flex items-center gap-3`}>
    {title ? <span className="text-[15px] font-semibold text-[var(--sc-text)]">{title}</span> : <Skeleton className="h-4 w-40" />}
    <Skeleton className="h-3 w-16 ml-auto" />
  </LoadingRegion>
);

// Sekcje z pytaniami (jak QuestionList)
export const SectionsSkeleton = ({ count = 3 }: { count?: number }) => (
  <LoadingRegion label="Wczytywanie sekcji raportu">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="bg-[var(--sc-surface)] border border-[var(--sc-border)] rounded-[12px] mb-4 overflow-hidden">
        <div className="flex items-center gap-[10px] px-4 py-3 bg-[var(--sc-bg)] border-b border-[var(--sc-border)]">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-3 w-12 ml-auto" />
        </div>
        {[0, 1, 2].map((q) => (
          <div key={q} className="flex items-center gap-4 px-4 py-3.5 border-b border-[var(--sc-border)] last:border-b-0">
            <SkeletonLines lines={q === 1 ? 2 : 1} className="flex-1" />
            <div className="flex gap-1.5 flex-shrink-0">
              <Skeleton className="h-7 w-12 rounded-full" />
              <Skeleton className="h-7 w-12 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    ))}
  </LoadingRegion>
);

// Cała strona, zanim przyjdzie raport
export const ReportPageSkeleton = () => (
  <div className="flex flex-col h-[calc(100vh-56px)]">
    <div className="bg-[var(--sc-surface)] border-b border-[var(--sc-border)] h-10 px-10 flex items-center gap-1.5 flex-shrink-0">
      <span className="text-[12px] text-[var(--sc-muted)]">Badania</span>
      <span className="text-[12px] text-[var(--sc-muted)]">›</span>
      <Skeleton className="h-3 w-28" />
      <span className="text-[12px] text-[var(--sc-muted)]">›</span>
      <Skeleton className="h-3 w-24" />
      <span className="text-[12px] text-[var(--sc-muted)]">›</span>
      <Skeleton className="h-3 w-16" />
    </div>
    <div className="flex flex-1 overflow-hidden">
      <aside className="w-[280px] bg-[var(--sc-surface)] border-r border-[var(--sc-border)] flex flex-col flex-shrink-0 overflow-hidden">
        <div className="px-5 pt-5 pb-4 border-b border-[var(--sc-border)]">
          <Skeleton className="h-3 w-20 mb-3" />
          <Skeleton className="h-8 w-full rounded-[8px]" />
        </div>
        <div className="px-4 py-3 flex flex-col gap-1.5 border-b border-[var(--sc-border)]">
          <Skeleton className="h-9 w-full rounded-[8px]" />
          <Skeleton className="h-9 w-full rounded-[8px]" />
        </div>
        <ReportHeaderSkeleton />
      </aside>
      <main className="flex-1 overflow-y-auto px-8 py-6 bg-[var(--sc-bg)]">
        <div className="flex flex-col gap-6">
          <div className={`${card} px-6 py-4`}>
            <Skeleton className="h-6 w-44" />
          </div>
          <ReportCardSkeleton />
          <ReportCardSkeleton />
          <SectionsSkeleton />
        </div>
      </main>
    </div>
  </div>
);
